import {groupByUniqueKey} from '@benchling/js-utils';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {TagSchema} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {getRequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';
import type {TagSchemaSelectorQuery} from 'schemas/shared/__generated/TagSchemaSelectorQuery';
import {TAG_SCHEMA_SELECTOR_QUERY} from 'schemas/shared/TagSchemaOrInterfaceSelector';

export interface SchemaPairing {
  formSchemaId: string;
  formSchemaName: string;
  compoundSchemaId: string;
  compoundSchemaName: string;
}

export interface UseMoleculeSchemasPairingResult {
  pairingMap: Record<string, SchemaPairing>;
  schemasById: Record<string, TagSchema>;
  isLoading: boolean;
}

/**
 * Given an array of TagSchema objects (REST format), builds a bidirectional
 * pairing map from form schemas to their parent (compound) schemas and vice versa.
 */
export function buildPairingMap(moleculeSchemas: Array<TagSchema>): Record<string, SchemaPairing> {
  const map: Record<string, SchemaPairing> = {};
  for (const schema of moleculeSchemas) {
    const parentLinkField = schema.fields?.find((field) => field.isParentLink);
    if (!parentLinkField) {
      continue;
    }

    const targetSchema = getRequiredLinkTagSchema(parentLinkField);
    if (!targetSchema) {
      continue;
    }

    const pairing: SchemaPairing = {
      formSchemaId: schema.id,
      formSchemaName: schema.name,
      compoundSchemaId: targetSchema.id,
      compoundSchemaName: targetSchema.name,
    };
    map[schema.id] = pairing;
    map[targetSchema.id] = pairing;
  }
  return map;
}

export default function useMoleculeSchemasPairing(
  registryId: string | null
): UseMoleculeSchemasPairingResult {
  const queryResponse = useBenchlingQuery<TagSchemaSelectorQuery>(TAG_SCHEMA_SELECTOR_QUERY);

  let moleculeSchemas: Array<TagSchema> = [];
  if (queryResponse.type === QueryResultType.SUCCESS) {
    moleculeSchemas = graphQLArrayToREST('TagSchema', queryResponse.data.tagSchemas).filter(
      (schema) =>
        schema.folderItemType === ModelTypeConstants.MOLECULE &&
        (registryId == null || schema.registryId === registryId)
    );
  }

  return {
    pairingMap: buildPairingMap(moleculeSchemas),
    schemasById: groupByUniqueKey(moleculeSchemas, (schema) => schema.id),
    isLoading: queryResponse.type === QueryResultType.PENDING,
  };
}
